import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faArrowDown, faMinus, faChevronDown, faChevronUp, faStar } from '@fortawesome/free-solid-svg-icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import './GlobalSummaryPage.css';

const apiUrl = process.env.REACT_APP_API_URL;

const GlobalSummaryPage = ({ token, businessIds, onBusinessClick }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRatingDetails, setShowRatingDetails] = useState(false);
  const [expandedOutlets, setExpandedOutlets] = useState({});

  const toggleOutletRating = (e, outletId) => {
    e.stopPropagation();
    setExpandedOutlets(prev => ({
      ...prev,
      [outletId]: !prev[outletId]
    }));
  };
  
  // Default to past 7 days
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  useEffect(() => {
    const fetchSummary = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${apiUrl}/business/global-summary?auth_token=${token}&start_date=${startDate}&end_date=${endDate}`);
        const result = await response.json();
        if (result.success) {
          setData(result.data);
        }
      } catch (error) {
        console.error("Error fetching global summary:", error);
      } finally {
        setLoading(false);
      }
    };
    if (token) {
      fetchSummary();
    }
  }, [token, startDate, endDate]);

  const calculateTrend = (current, comparison) => {
    if (!comparison || comparison === 0) return current > 0 ? { value: 100, type: 'gs-positive' } : { value: 0, type: 'gs-neutral' };
    const diff = current - comparison;
    const percentage = (diff / comparison) * 100;
    
    if (percentage > 0) return { value: percentage.toFixed(1), type: 'gs-positive' };
    if (percentage < 0) return { value: Math.abs(percentage).toFixed(1), type: 'gs-negative' };
    return { value: 0, type: 'gs-neutral' };
  };

  const renderTrendBadge = (trend) => {
    let icon = faMinus;
    if (trend.type === 'gs-positive') icon = faArrowUp;
    if (trend.type === 'gs-negative') icon = faArrowDown;

    return (
      <div className={`gs-trend-badge ${trend.type}`}>
        <FontAwesomeIcon icon={icon} className="gs-trend-icon" />
        {trend.value}%
      </div>
    );
  };

  if (loading || !data) {
    return <div className="gs-loading-container">Loading Analytics...</div>;
  }

  const { summary, outlets } = data;

  // Prepare chart data (Top 5 outlets by visits)
  const sortedOutlets = [...outlets].sort((a, b) => (b.current?.visits || 0) - (a.current?.visits || 0)).slice(0, 5);
  const chartData = sortedOutlets.map(o => ({
    name: o.name,
    'Current Period': o.current?.visits || 0,
    'Previous Period': o.comparison?.visits || 0,
  }));

  return (
    <div className="gs-global-summary-page">
      <div className="gs-summary-header">
        <h1>Global Summary</h1>
        <div className="gs-date-range-picker">
          <input 
            type="date" 
            value={startDate} 
            onChange={(e) => setStartDate(e.target.value)} 
          />
          <span>to</span>
          <input 
            type="date" 
            value={endDate} 
            onChange={(e) => setEndDate(e.target.value)} 
          />
        </div>
      </div>

      <div className="gs-summary-cards">
        <div className="gs-summary-card-item">
          <div className="gs-card-title">Total Visits</div>
          <div className="gs-card-value">{summary.current?.visits || 0}</div>
          {renderTrendBadge(calculateTrend(summary.current?.visits || 0, summary.comparison?.visits || 0))}
        </div>
        <div className="gs-summary-card-item">
          <div className="gs-card-title">New Customers</div>
          <div className="gs-card-value">{summary.current?.total_customers || 0}</div>
          {renderTrendBadge(calculateTrend(summary.current?.total_customers || 0, summary.comparison?.total_customers || 0))}
        </div>
        <div className="gs-summary-card-item">
          <div className="gs-card-title">Revenue</div>
          <div className="gs-card-value">₹{(summary.current?.revenue || 0).toLocaleString()}</div>
          {renderTrendBadge(calculateTrend(summary.current?.revenue || 0, summary.comparison?.revenue || 0))}
        </div>
        <div className="gs-summary-card-item">
          <div className="gs-card-title">Avg Rating</div>
          <div className="gs-card-value">{(summary.current?.avg_rating || 0).toFixed(1)}</div>
          <div>
            {renderTrendBadge(calculateTrend(summary.current?.avg_rating || 0, summary.comparison?.avg_rating || 0))}
          </div>
          <button 
            className="gs-expand-btn" 
            onClick={() => setShowRatingDetails(!showRatingDetails)}
          >
            {showRatingDetails ? 'Hide breakdown' : 'View breakdown'}
            <FontAwesomeIcon icon={showRatingDetails ? faChevronUp : faChevronDown} />
          </button>
          
          {showRatingDetails && (
            <div className="gs-rating-details">
              <div className="gs-rating-row">
                <div className="gs-rating-label">
                  5 <FontAwesomeIcon icon={faStar} className="gs-star-icon" />
                </div>
                <div className="gs-rating-count">{summary.current?.['5_Star'] || 0} Ratings</div>
              </div>
              <div className="gs-rating-row">
                <div className="gs-rating-label">
                  4 <FontAwesomeIcon icon={faStar} className="gs-star-icon" />
                </div>
                <div className="gs-rating-count">{summary.current?.['4_Star'] || 0} Ratings</div>
              </div>
              <div className="gs-rating-row">
                <div className="gs-rating-label">
                  ≤ 3 <FontAwesomeIcon icon={faStar} className="gs-star-icon" />
                </div>
                <div className="gs-rating-count">{summary.current?.['3_Star_or_less'] || 0} Ratings</div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="gs-charts-section">
        <div className="gs-chart-container">
          <h3>Top Outlets by Visits</h3>
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#6B7280', fontSize: 12}} />
                <YAxis axisLine={false} tickLine={false} tick={{fill: '#6B7280', fontSize: 12}} />
                <RechartsTooltip cursor={{fill: '#F3F4F6'}} contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)'}} />
                <Legend iconType="circle" wrapperStyle={{fontSize: '12px', color: '#374151'}} />
                <Bar dataKey="Current Period" fill="#3f51b5" radius={[4, 4, 0, 0]} barSize={32} />
                <Bar dataKey="Previous Period" fill="#9CA3AF" radius={[4, 4, 0, 0]} barSize={32} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="gs-outlets-section">
        <h3>Outlet Insights</h3>
        <div className="gs-outlet-list">
          {outlets.map(outlet => {
            const vTrend = calculateTrend(outlet.current?.visits || 0, outlet.comparison?.visits || 0);
            const rTrend = calculateTrend(outlet.current?.revenue || 0, outlet.comparison?.revenue || 0);
            return (
              <div key={outlet.id} className="gs-outlet-wrapper" onClick={() => onBusinessClick(parseInt(outlet.id, 10))}>
                <div className="gs-outlet-item">
                  <div className="gs-outlet-info-col">
                    <div className="gs-outlet-name">{outlet.name}</div>
                    <div className="gs-outlet-sub">Tap to view customers</div>
                  </div>
                  <div className="gs-outlet-stats">
                    <div className="gs-stat-block">
                      <div className="gs-stat-label">Visits</div>
                      <div className="gs-stat-val">{outlet.current?.visits || 0}</div>
                      <div className={`gs-stat-trend ${vTrend.type}`}>
                        {vTrend.value > 0 && vTrend.type === 'gs-positive' && '+'}{vTrend.value}%
                      </div>
                    </div>
                    <div className="gs-stat-block">
                      <div className="gs-stat-label">Revenue</div>
                      <div className="gs-stat-val">₹{(outlet.current?.revenue || 0).toLocaleString()}</div>
                      <div className={`gs-stat-trend ${rTrend.type}`}>
                        {rTrend.value > 0 && rTrend.type === 'gs-positive' && '+'}{rTrend.value}%
                      </div>
                    </div>
                    <div className="gs-stat-block">
                      <div className="gs-stat-label">Rating</div>
                      <div className="gs-stat-val">{(outlet.current?.avg_rating || 0).toFixed(1)}</div>
                      <button 
                        className="gs-expand-btn" 
                        onClick={(e) => toggleOutletRating(e, outlet.id)}
                      >
                        {expandedOutlets[outlet.id] ? 'Hide' : 'View'}
                        <FontAwesomeIcon icon={expandedOutlets[outlet.id] ? faChevronUp : faChevronDown} />
                      </button>
                    </div>
                  </div>
                </div>
                
                {expandedOutlets[outlet.id] && (
                  <div className="gs-outlet-details-box" onClick={(e) => e.stopPropagation()}>
                    <div className="gs-rating-row">
                      <div className="gs-rating-label">
                        5 <FontAwesomeIcon icon={faStar} className="gs-star-icon" />
                      </div>
                      <div className="gs-rating-count">{outlet.current?.['5_Star'] || 0}</div>
                    </div>
                    <div className="gs-rating-row">
                      <div className="gs-rating-label">
                        4 <FontAwesomeIcon icon={faStar} className="gs-star-icon" />
                      </div>
                      <div className="gs-rating-count">{outlet.current?.['4_Star'] || 0}</div>
                    </div>
                    <div className="gs-rating-row">
                      <div className="gs-rating-label">
                        ≤ 3 <FontAwesomeIcon icon={faStar} className="gs-star-icon" />
                      </div>
                      <div className="gs-rating-count">{outlet.current?.['3_Star_or_less'] || 0}</div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default GlobalSummaryPage;
